import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { IntegrationNamePreview } from '@modules/integrations/components/IntegrationNamePreview';
import { trackIntegrationEvent } from '@modules/integrationsV2/utils';
import { LoadingButton } from '@mui/lab';
import { DialogActions, DialogContent, Stack } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDirection,
  IntegrationType,
} from 'generated/graphql';
import { IntegrationNameErrorMessages } from 'modules/ui/Settings/Integrations/utils/integrationErrorMessages';
import { ChangeEvent, useState } from 'react';
import ErrorCode from 'v2/core/errors/codes';
import { isErrorCode } from 'v2/core/errors/errors';
import useCreateIntegrationEdgeServer from 'v2/core/repository/edge-server/integrations';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';
import { SnowflakeFormTextInputs } from './SnowflakeFormTextInputs';

export interface SnowflakeFormData {
  accountName: string;
  database: string;
  schema: string;
  warehouse: string;
  table: {
    errorMessage: null | string;
    value: string;
  };
  user: string;
  role: string;
}

const initialState: SnowflakeFormData = {
  accountName: '',
  database: '',
  schema: '',
  warehouse: '',
  table: {
    errorMessage: null,
    value: '',
  },
  user: '',
  role: '',
};

interface SnowflakeFormModalProps {
  integrationType: IntegrationType;
}

export const SnowflakeFormModal = ({
  integrationType,
}: SnowflakeFormModalProps) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();
  const { closeModal } = useMuiModalContext();
  const { showErrorNotification } = useNotificationContext();

  const [formData, setFormData] = useState<SnowflakeFormData>(initialState);

  const { status, createIntegration } = useCreateIntegrationEdgeServer({
    tenantID,
    direction: IntegrationDirection.Inbound,
    channel: IntegrationChannelType.Snowflake,
    integrationType,
    createdByUser: userID,
    inputData: {
      snowflake: {
        accountName: formData.accountName,
        database: formData.database,
        schema: formData.schema,
        warehouse: formData.warehouse,
        table: formData.table.value,
        user: formData.user,
        role: formData.role,
      },
    },
    onError: (error: Error) => {
      if (isErrorCode(error, ErrorCode.DuplicateRecord)) {
        setFormData({
          ...formData,
          table: {
            value: formData.table.value,
            errorMessage: IntegrationNameErrorMessages.DUPLICATE_NAME,
          },
        });
        return;
      }
      showErrorNotification({
        contentComponent: `Something went wrong${
          error.message ? `: ${error.message}` : '!'
        }`,
        props: {},
      });
    },
    onCompleted: () => {
      closeModal();
      trackIntegrationEvent({
        eventName: 'Integration Added',
        integrationChannelType: IntegrationChannelType.Snowflake,
      });
    },
  });

  const handleSubmit = () => {
    createIntegration();
    trackIntegrationEvent({
      eventName: 'Integration Form Connect CTA Clicked',
      integrationChannelType: IntegrationChannelType.Snowflake,
    });
  };

  const loading = IsLoading(status);

  const handleTextInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === 'table') {
      if (value.length > 100) {
        return setFormData({
          ...formData,
          [name]: {
            value,
            errorMessage: IntegrationNameErrorMessages.CHARACTER_LIMIT_EXCEEDED,
          },
        });
      }

      if (value.includes('-')) {
        return setFormData({
          ...formData,
          [name]: {
            value,
            errorMessage: IntegrationNameErrorMessages.DASH_UNSUPPORTED,
          },
        });
      }
      return setFormData({
        ...formData,
        [name]: {
          value,
          errorMessage: null,
        },
      });
    }
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const isConnectButtonDisabled =
    !formData.accountName ||
    !formData.database ||
    !formData.schema ||
    !formData.warehouse ||
    !formData.table.value ||
    Boolean(formData.table.errorMessage);

  return (
    <>
      <ModalHeader title="Snowflake" showCloseButton={true} />
      <DialogContent dividers>
        <Stack pt={1} spacing={2}>
          <SnowflakeFormTextInputs
            formData={formData}
            handleTextInputChange={handleTextInputChange}
            isLoading={loading}
          />
          <IntegrationNamePreview
            integrationName={formData.table.value}
            sourceName={IntegrationChannelType.Snowflake}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <LoadingButton
          variant="contained"
          type="submit"
          size="large"
          fullWidth
          onClick={handleSubmit}
          loading={loading}
          disabled={isConnectButtonDisabled}
        >
          Connect
        </LoadingButton>
      </DialogActions>
    </>
  );
};
